import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { Tag } from "entities/shared/tags.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { useAirGetTagsQuery } from "entities/AirTags";
import { authStore } from "entities/Auth";
import { searchFilterStore } from "entities/SearchFilter";
import { useSavedCardsByTagsQuery, UseSavedCardsByTagsQueryReturnType } from "entities/SeaSavedCard";
import { useSeaGetTagsQuery } from "entities/SeaTags";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";

import { tagsStore } from "../model/tags.store";

type OpenTagFunc = (tagId: string) => void;

type TagsContextType = UseSavedCardsByTagsQueryReturnType & {
  tagsList: Tag[];
  openTag: OpenTagFunc;
  activeTag: string | null;
  refreshTags: () => void;
  allTags: Array<string>;
  setAllTags: Dispatch<SetStateAction<Array<string>>>;
  setActiveTag: Dispatch<SetStateAction<string | null>>;
};

const TagsContext = React.createContext(null as unknown as TagsContextType);

type TagsProviderProps = {
  children: React.ReactNode;
};

export const TagsProvider: RFC<TagsProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const { currentView, showCardsByTagView, isCurrentViewCardsByTag, isCurrentViewOpenedCard } = usePanelViews();
  const { seaTagsData, seaTagsRequest, resetSeaTagsQuery } = useSeaGetTagsQuery();
  const { airTagsData, airTagsRequest, resetAirTagsQuery } = useAirGetTagsQuery();
  const [allTags, setAllTags] = useState<Array<string>>([]);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [tagsList, setTagsList] = useState<Tag[]>([]);

  const useSavedCardsByTagsQueryValue = useSavedCardsByTagsQuery();

  const tagsData = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return seaTagsData;
      case ShipmentType.AIR:
        return airTagsData;
    }
  }, [airTagsData, seaTagsData, shipment]);

  const tagsRequest = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return seaTagsRequest;
      case ShipmentType.AIR:
        return airTagsRequest;
    }
  }, [airTagsRequest, seaTagsRequest, shipment]);

  const resetTagsQuery = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return resetSeaTagsQuery;
      case ShipmentType.AIR:
        return resetAirTagsQuery;
    }
  }, [resetAirTagsQuery, resetSeaTagsQuery, shipment]);

  useEffect(() => {
    if (!isAuthenticated) return;

    tagsRequest?.();
  }, [isAuthenticated, shipment]);

  useEffect(() => {
    if (tagsData) {
      setTagsList(tagsData);
      setAllTags(tagsData.map(({ name }) => name));
    }
  }, [tagsData]);

  useEffect(() => {
    if (!isCurrentViewCardsByTag && !isCurrentViewOpenedCard) {
      setActiveTag(null);
      dispatch(tagsStore.actions.clearCardsByTagAction());
    }
  }, [currentView]);

  const refreshTags = () => {
    const reset = resetTagsQuery?.();

    if (!reset) {
      tagsRequest?.();
      return;
    }

    reset.finally(() => {
      tagsRequest?.();
    });
  };

  const openTag: OpenTagFunc = (tagId) => {
    setActiveTag(tagId);
    showCardsByTagView();
    dispatch(tagsStore.actions.setCurrentTag(tagId));
  };

  return (
    <TagsContext.Provider
      value={{
        ...useSavedCardsByTagsQueryValue,
        openTag,
        refreshTags,
        setAllTags,
        setActiveTag,
        tagsList,
        activeTag,
        allTags,
      }}
    >
      {children}
    </TagsContext.Provider>
  );
};

export const useTagsContext = () => {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error("No context found for Tags");
  }
  return context;
};
